/**
 * <countdown-banner> Web Component
 *
 * Drives the header countdown bar. The launch/start times are resolved to Unix
 * epochs in Liquid (store timezone, DST-aware), so this only ever compares
 * integers — every visitor counts down to the same instant.
 *
 * connectedCallback/disconnectedCallback fire on shopify:section:load and
 * shopify:section:unload, so the interval is set up and torn down automatically
 * in the theme editor without stacking.
 *
 * Usage (in Liquid):
 *   <countdown-banner
 *     data-launch-at="1792166400"
 *     data-start-at="0"
 *     data-server-now="1785000000"
 *     data-expiry-behavior="show_message"
 *     data-expired-link="/collections/new"
 *   >
 *     <span data-cd-days></span> ... <div data-cd-live> <div data-cd-expired hidden>
 *   </countdown-banner>
 */

// Only trust the server clock over the visitor's when they disagree by more
// than this. A genuinely wrong device clock is off by hours; a small gap is
// just page-cache age and correcting for it would do more harm than good.
const CLOCK_SKEW_THRESHOLD_MS = 5 * 60 * 1000;

const MS = { second: 1000, minute: 60000, hour: 3600000, day: 86400000 };

class CountdownBanner extends HTMLElement {
  connectedCallback() {
    this.launchAt = Number(this.dataset.launchAt || 0) * 1000;
    this.startAt = Number(this.dataset.startAt || 0) * 1000;
    this.expiryBehavior = this.dataset.expiryBehavior || 'show_message';

    if (!this.launchAt) return;

    this.daysEl = this.querySelector('[data-cd-days]');
    this.hoursEl = this.querySelector('[data-cd-hours]');
    this.minutesEl = this.querySelector('[data-cd-minutes]');
    this.secondsEl = this.querySelector('[data-cd-seconds]');
    this.daysUnit = this.querySelector('[data-cd-unit="days"]');
    this.hoursUnit = this.querySelector('[data-cd-unit="hours"]');
    this.liveEl = this.querySelector('[data-cd-live]');
    this.expiredEl = this.querySelector('[data-cd-expired]');
    this.announcementEl = this.querySelector('[data-cd-announcement]');
    this.linkEl = this.querySelector('[data-cd-link]');

    this.skew = this.calculateSkew();
    this.lastAnnouncedMinute = null;

    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // Start the interval before the first tick: if the launch has already
    // passed, that tick calls expire() and must be able to clear it.
    this.interval = setInterval(() => this.tick(), MS.second);
    this.tick();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  stop() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  /**
   * Offset between the server's clock and this device's, in ms. Ignored unless
   * the gap is large enough to indicate a genuinely misconfigured device.
   */
  calculateSkew() {
    const serverNow = Number(this.dataset.serverNow || 0) * 1000;
    if (!serverNow) return 0;

    const skew = serverNow - Date.now();
    return Math.abs(skew) > CLOCK_SKEW_THRESHOLD_MS ? skew : 0;
  }

  get now() {
    return Date.now() + this.skew;
  }

  get isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  handleVisibilityChange() {
    // A backgrounded tab throttles setInterval, so re-sync the moment it wakes.
    if (!document.hidden) this.tick();
  }

  tick() {
    const now = this.now;

    // Scheduled to appear later — stay out of the way, but keep it visible in
    // the editor so the merchant can still see and select the section.
    if (this.startAt && now < this.startAt) {
      this.hidden = !this.isDesignMode;
      return;
    }

    const remaining = this.launchAt - now;

    if (remaining <= 0) {
      this.expire();
      return;
    }

    this.hidden = false;
    this.render(remaining);
  }

  render(remaining) {
    const days = Math.floor(remaining / MS.day);
    const hours = Math.floor((remaining % MS.day) / MS.hour);
    const minutes = Math.floor((remaining % MS.hour) / MS.minute);
    const seconds = Math.floor((remaining % MS.minute) / MS.second);

    // Collapse leading units that have run down. The CSS hides the trailing
    // separator alongside the unit.
    this.daysUnit?.classList.toggle('is-collapsed', days === 0);
    this.hoursUnit?.classList.toggle('is-collapsed', days === 0 && hours === 0);

    if (this.daysEl) this.daysEl.textContent = pad(days);
    if (this.hoursEl) this.hoursEl.textContent = pad(hours);
    if (this.minutesEl) this.minutesEl.textContent = pad(minutes);
    if (this.secondsEl) this.secondsEl.textContent = pad(seconds);

    this.announce(days, hours, minutes);
  }

  /** Refresh the screen-reader summary once a minute, not once a second. */
  announce(days, hours, minutes) {
    if (!this.announcementEl || minutes === this.lastAnnouncedMinute) return;
    this.lastAnnouncedMinute = minutes;

    const parts = [];
    if (days > 0) parts.push(`${days} ${plural(days, 'day')}`);
    if (days > 0 || hours > 0) parts.push(`${hours} ${plural(hours, 'hour')}`);
    parts.push(`${minutes} ${plural(minutes, 'minute')}`);

    this.announcementEl.textContent = `${listify(parts)} remaining.`;
  }

  expire() {
    this.stop();

    const canShowMessage = this.expiryBehavior === 'show_message' && this.expiredEl;

    if (!canShowMessage) {
      this.hidden = !this.isDesignMode;
      return;
    }

    if (this.liveEl) this.liveEl.hidden = true;
    this.expiredEl.hidden = false;
    this.hidden = false;

    if (this.linkEl) {
      const expiredLink = this.dataset.expiredLink;
      if (expiredLink) {
        this.linkEl.href = expiredLink;
      } else {
        this.linkEl.remove();
        this.linkEl = null;
      }
    }
  }
}

function pad(value) {
  return String(value).padStart(2, '0');
}

function plural(value, word) {
  return value === 1 ? word : `${word}s`;
}

function listify(parts) {
  if (parts.length < 2) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

if (!customElements.get('countdown-banner')) {
  customElements.define('countdown-banner', CountdownBanner);
}
